// PURPOSE: Show related records by name in tables instead of their ids.
// CONTRACT: Spec 082; one cached list read per related model, member relations read the member options endpoint.
// DEPENDS: Session API, React Query, recordTitle.
// CEILING: Read-only lookup; an unreadable relation falls back to a short id, never an error.
import { useQuery } from '@tanstack/react-query';
import { useSession } from './auth';
import { recordTitle } from './domain';
import type { FieldDefinition } from './types';

export function useRelationNames(model: string) {
  const { api } = useSession();
  return useQuery({ queryKey: ['relation-names', model], enabled: !!model, staleTime: 60_000, retry: false, queryFn: async () => {
    const names = new Map<string, string>();
    if (/^(membership|member|user)$/.test(model)) {
      const result = await api.request<{ items: { id: string; name: string }[] }>('/members/options');
      for (const item of result.items) names.set(item.id, item.name);
      return names;
    }
    for (let offset = 0; offset < 1000;) {
      const page = await api.list(model, { limit: 200, offset });
      for (const item of page.items) names.set(item.id, recordTitle(item));
      offset += page.items.length;
      if (!page.items.length || offset >= page.total) break;
    }
    return names;
  } });
}

export function RelationNames({ field, value }: { field: FieldDefinition; value: unknown }) {
  const model = typeof field.relation === 'object' ? field.relation.model : String(field.relation || '');
  const names = useRelationNames(model);
  const ids = (Array.isArray(value) ? value : [value]).map(String).filter(Boolean);
  if (!ids.length) return null;
  const label = (id: string) => names.data?.get(id) || (names.isLoading ? '…' : id.slice(0, 8));
  return <>{ids.map(label).join(', ')}</>;
}
